import axios, { InternalAxiosRequestConfig } from 'axios';
import API_ENDPOINTS from '../config/api';
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  ApiResponse,
  User,
} from '../utils/types';
import toast from 'react-hot-toast';
import { handleApiError } from '../utils/apiErrorHandler';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
  _retry?: boolean;
}

const api = axios.create({
  baseURL: API_ENDPOINTS.BASE,
});

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

api.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined') {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.metadata = { startTime: new Date() };
    return config;
  },
  error => {
    toast.error('Request failed');
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    if (response.config.responseType === 'blob') {
      return response;
    }
    if (response.data && typeof response.data === 'object') {
      if ('success' in response.data || 'data' in response.data) {
        if (
          response.data.success &&
          response.data.data &&
          typeof response.data.data === 'object'
        ) {
          if (response.data.data.success === false) {
            return {
              ...response,
              data: {
                success: false,
                error: response.data.data.error,
                message:
                  response.data.data.error ||
                  response.data.data.message ||
                  'Request failed',
              },
            };
          }
          if (response.data.data.data) {
            return {
              ...response,
              data: {
                success: response.data.success,
                data: response.data.data.data,
                message:
                  response.data.message ||
                  response.data.data.message ||
                  'Request successful',
              },
            };
          }
          return {
            ...response,
            data: {
              success: response.data.success,
              data: response.data.data,
              message:
                response.data.message ||
                response.data.data.message ||
                'Request successful',
            },
          };
        }
        return response;
      }

      return {
        ...response,
        data: {
          success: true,
          data: response.data,
          message:
            (response.data as { message?: string }).message ||
            'Request successful',
        },
      };
    }

    return response;
  },
  async error => {
    const originalConfig = error.config as
      | ExtendedAxiosRequestConfig
      | undefined;
    const status = error?.response?.status;

    const isRefreshRequest =
      originalConfig?.url &&
      typeof originalConfig.url === 'string' &&
      originalConfig.url.includes('/auth/refresh');

    if (
      status === 401 &&
      originalConfig &&
      !originalConfig._retry &&
      !isRefreshRequest
    ) {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (storedRefreshToken) {
        originalConfig._retry = true;

        try {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                const refreshResponse =
                  await authAPI.refreshToken(storedRefreshToken);
                const newAccessToken = refreshResponse.token as
                  | string
                  | undefined;
                const newRefreshToken = refreshResponse.refreshToken as
                  | string
                  | undefined;

                if (newAccessToken) {
                  localStorage.setItem('token', newAccessToken);
                  api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
                }
                if (newRefreshToken) {
                  localStorage.setItem('refreshToken', newRefreshToken);
                }
                return newAccessToken ?? null;
              } finally {
                isRefreshing = false;
              }
            })();
          }

          const newToken = await refreshPromise;

          if (newToken) {
            originalConfig.headers = originalConfig.headers ?? {};
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
            return api(originalConfig);
          }
        } catch (refreshError) {
          // Refresh failed – fall through to normal error handler
        }
      }
    }

    handleApiError(error);
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return { ...responseData.data, success: true };
    }

    if ('token' in responseData || 'user' in responseData) {
      return { ...responseData, success: true } as unknown as AuthResponse;
    }

    throw new Error(responseData.message || 'Login failed');
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const formData = new FormData();
    const dataObj = data as unknown as Record<string, unknown>;

    Object.entries(dataObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (!(value instanceof File)) {
          let finalValue: unknown = value;
          if (
            key === 'dob' &&
            typeof value === 'string' &&
            value.includes('-')
          ) {
            const parts = value.split('-');
            if (parts.length === 3 && parts[0].length === 4) {
              finalValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
          }
          formData.append(key, String(finalValue));
        }
      }
    });

    if (data.facialVerification instanceof File) {
      formData.append(
        'facialVerification',
        data.facialVerification,
        data.facialVerification.name
      );
    }
    if (data.addressDocument instanceof File) {
      formData.append(
        'addressDocument',
        data.addressDocument,
        data.addressDocument.name
      );
    }

    try {
      const response = await api.post<ApiResponse<AuthResponse>>(
        API_ENDPOINTS.AUTH.SIGNUP,
        formData
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return { ...responseData.data, success: true };
      }

      if ('token' in responseData || 'user' in responseData) {
        return { ...responseData, success: true } as unknown as AuthResponse;
      }

      throw new Error(responseData.message || 'Registration failed');
    } catch (error: any) {
      if (error.response) {
        console.error(
          '❌ Error response:',
          error.response.status,
          error.response.data
        );
      }
      throw error;
    }
  },

  logout: async (): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGOUT);
    return response.data;
  },

  sendEmailOTP: async (email: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.SEND_EMAIL_OTP, {
      email,
    });
    return response.data;
  },

  verifyEmailOTP: async (email: string, otp: number): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP, {
      email,
      otp,
    });
    return response.data;
  },

  resendEmailOTP: async (email: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.SEND_EMAIL_OTP, {
      email,
    });
    return response.data;
  },

  sendPhoneOTP: async (phone: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.SEND_PHONE_OTP, {
      phone,
    });
    return response.data;
  },

  verifyPhoneOTP: async (phone: string, otp: number): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_PHONE_OTP, {
      phone,
      otp,
    });
    return response.data;
  },

  resendPhoneOTP: async (phone: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.SEND_PHONE_OTP, {
      phone,
    });
    return response.data;
  },

  forgotPassword: async (email: string, newPassword: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email,
      newPassword,
    });
    return response.data;
  },

  getProfile: async (id: string | number): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.USERS.GET_PROFILE(id));
    const responseData = response.data;

    const strippedData = JSON.parse(JSON.stringify(responseData));
    const stripTimestamps = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;
      delete obj.createdAt;
      delete obj.updatedAt;
      const values = Object.keys(obj).map(key => obj[key]);
      values.forEach(value => stripTimestamps(value));
    };
    stripTimestamps(strippedData);

    if (responseData.success && responseData.data) {
      const { user, verification, bankDetails } = responseData.data;
      return {
        ...user,
        verification,
        bankDetails,
      };
    }

    return responseData;
  },

  updateProfile: async (
    id: string | number,
    data: Partial<User>
  ): Promise<any> => {
    const response = await api.put(
      API_ENDPOINTS.USERS.UPDATE_PROFILE(id),
      data
    );
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data;
    }

    return responseData;
  },

  updateBankDetails: async (id: string | number, data: any): Promise<any> => {
    const isNewFile =
      data.verificationDocument instanceof File ||
      data.verificationDocument instanceof Blob;
    let payload: any;

    if (isNewFile) {
      payload = new FormData();
      const dataObj = data as unknown as Record<string, unknown>;

      Object.entries(dataObj).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          key !== 'cancelledCheque'
        ) {
          if (key === 'verificationDocument') {
            if (value instanceof File || value instanceof Blob) {
              payload.append('verificationDocument', value);
            }
          } else if (typeof value === 'boolean') {
            payload.append(key, value ? 'true' : 'false');
          } else {
            payload.append(key, String(value));
          }
        }
      });
    } else {
      payload = {
        accountName: data.accountName,
        accountNumber: data.accountNumber,
        bankName: data.bankName,
        bankCountry: data.bankCountry,
        currency: data.currency,
        branchName: data.branchName,
        ifscCode: data.ifscCode,
        routingNumber: data.routingNumber,
        sortCode: data.sortCode,
        bsbNumber: data.bsbNumber,
        iban: data.iban,
        bicSwift: data.bicSwift,
        verificationDocumentType: data.verificationDocumentType,
        verificationDocument: data.verificationDocument,
        isPrimary: data.isPrimary,
        isActive: data.isActive,
      };
    }

    const response = await api.put(
      API_ENDPOINTS.USERS.UPDATE_BANK_DETAILS(id),
      payload
    );
    const responseData = response.data;
    return responseData.success && responseData.data
      ? responseData.data
      : responseData;
  },

  updateFacialVerification: async (
    id: string | number,
    file: File
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('facialVerification', file);

    const response = await api.put(
      API_ENDPOINTS.USERS.UPDATE_FACIAL_VERIFICATION(id),
      formData
    );
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data;
    }

    return responseData;
  },

  refreshToken: async (refreshToken: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data;
    }

    return responseData;
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return {
        success: responseData.success,
        message: responseData.message,
        ...responseData.data,
      };
    }

    return responseData;
  },
};

export const clientsAPI = {
  getAll: async (params?: any): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.CLIENTS.GET_ALL, { params });
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.CLIENTS.GET_BY_ID(id));
    return response.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.CLIENTS.CREATE, data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<any> => {
    const response = await api.put(API_ENDPOINTS.CLIENTS.UPDATE(id), data);
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await api.delete(API_ENDPOINTS.CLIENTS.DELETE(id));
    return response.data;
  },
};

export const tradesAPI = {
  getAll: async (params?: any): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.TRADES.GET_ALL, { params });
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.TRADES.GET_BY_ID(id));
    return response.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.TRADES.CREATE, data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<any> => {
    const response = await api.put(API_ENDPOINTS.TRADES.UPDATE(id), data);
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await api.delete(API_ENDPOINTS.TRADES.DELETE(id));
    return response.data;
  },
};

export const adminAPI = {
  getDashboard: async (): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.ADMIN.DASHBOARD);
    return response.data;
  },

  getUsers: async (params?: any): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.ADMIN.USERS, { params });
    return response.data;
  },

  getUsersList: async (params?: any): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.ADMIN.USERS, { params });
      return response.data;
    } catch (err: any) {
      throw new Error(
        `Failed to fetch users list: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  },

  updateUserStatus: async (
    userId: string | number,
    statusData: { status: string; reason?: string }
  ): Promise<any> => {
    try {
      const response = await api.put(
        API_ENDPOINTS.ADMIN.UPDATE_USER_STATUS(userId),
        statusData
      );
      return response.data;
    } catch (err: any) {
      throw new Error(
        `Failed to update user status: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  },

  getUserById: async (userId: string | number): Promise<any> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.ADMIN.GET_USER_BY_ID(userId)
      );
      return response.data;
    } catch (err: any) {
      throw new Error(
        `Failed to fetch user: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  },

  updateUser: async (userId: string | number, userData: any): Promise<any> => {
    try {
      const response = await api.put(
        API_ENDPOINTS.ADMIN.UPDATE_USER(userId),
        userData
      );
      return response.data;
    } catch (err: any) {
      throw new Error(
        `Failed to update user: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  },

  deleteUser: async (userId: string | number): Promise<any> => {
    try {
      const response = await api.delete(
        API_ENDPOINTS.ADMIN.DELETE_USER(userId)
      );
      return response.data;
    } catch (err: any) {
      throw new Error(
        `Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  },

  createClient: async (data: {
    fullName: string;
    email: string;
    phone: string;
    password: string;
    dob?: string;
    source?: string;
    role?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.ADMIN.CREATE_CLIENT, data);
    return response.data;
  },
};

export const accountAPI = {
  createAccount: async (data: any): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.ACCOUNT.CREATE, data);
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data;
    }
    return responseData;
  },

  validateReferralCode: async (referralCode: string): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.ACCOUNT.VALIDATE_REFERRAL, {
      referralCode,
    });
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data;
    }
    return responseData;
  },

  exportData: async (): Promise<Blob> => {
    const response = await api.get<Blob>(API_ENDPOINTS.ACCOUNT.EXPORT_DATA, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const errorBody = JSON.parse(await response.data.text());
      throw new Error(errorBody.message || 'Failed to download data');
    }

    return response.data;
  },
};

export const verificationAPI = {
  verifyAadhaar: async (data: {
    name: string;
    dob: string;
    aadhaarNumber: string;
    document: File;
  }): Promise<any> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('dob', data.dob);
    formData.append('aadhaar_number', data.aadhaarNumber);
    formData.append('document', data.document);

    const response = await api.post(API_ENDPOINTS.VERIFY.AADHAAR, formData);
    return response.data;
  },

  storeAadhaarVerificationData: async (data: {
    aadharNumber: string;
    isAadhaarVerified: boolean;
    aadharVerifiedDate: string;
  }): Promise<any> => {
    const response = await api.post(
      API_ENDPOINTS.KYC.STORE_AADHAAR_VERIFICATION,
      data
    );
    return response.data;
  },

  verifyPan: async (data: {
    name: string;
    dob: string;
    panNumber: string;
    document: File;
  }): Promise<any> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('dob', data.dob);
    formData.append('pan_number', data.panNumber);
    formData.append('document', data.document);

    const response = await api.post(API_ENDPOINTS.VERIFY.PAN, formData);
    return response.data;
  },

  storePanVerificationData: async (data: {
    panNumber: string;
    isPanVerified: boolean;
    panVerifiedDate: string;
  }): Promise<any> => {
    const response = await api.post(
      API_ENDPOINTS.KYC.STORE_PAN_VERIFICATION,
      data
    );
    return response.data;
  },

  verifyPassport: async (data: {
    name: string;
    dob: string;
    passportNumber: string;
    documentFront: File;
    documentBack: File;
  }): Promise<any> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('dob', data.dob);
    formData.append('passport_number', data.passportNumber);
    formData.append('document_front', data.documentFront);
    formData.append('document_back', data.documentBack);

    const response = await api.post(API_ENDPOINTS.VERIFY.PASSPORT, formData);
    return response.data;
  },

  storePassportVerificationData: async (data: {
    passportNumber: string;
    isPassportVerified: boolean;
    passportVerifiedDate: string;
  }): Promise<any> => {
    const response = await api.post(
      API_ENDPOINTS.KYC.STORE_PASSPORT_VERIFICATION,
      data
    );
    return response.data;
  },

  storeFacialVerificationData: async (data: {
    facialVerification: File;
  }): Promise<any> => {
    const formData = new FormData();
    formData.append('facialVerification', data.facialVerification);

    const response = await api.post(
      API_ENDPOINTS.KYC.STORE_FACIAL_VERIFICATION,
      formData
    );
    return response.data;
  },
};

export const bankAPI = {
  addBankDetails: async (data: {
    accountName: string;
    accountNumber?: string;
    bankName: string;
    bankCountry?: string;
    currency?: string;
    branchName?: string;
    ifscCode?: string;
    routingNumber?: string;
    sortCode?: string;
    bsbNumber?: string;
    iban?: string;
    bicSwift?: string;
    verificationDocumentType?: string;
    verificationDocument?: File | null;
  }): Promise<any> => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        !(value instanceof File) &&
        key !== 'cancelledCheque'
      ) {
        formData.append(key, String(value));
      }
    });
    if (data.verificationDocument instanceof File) {
      formData.append('verificationDocument', data.verificationDocument);
    }

    const response = await api.post(
      API_ENDPOINTS.BANK.ADD_BANK_DETAILS,
      formData
    );
    return response.data;
  },
};

export const supportTicketAPI = {
  createTicket: async (data: {
    subject: string;
    category:
      | 'Trading Issues'
      | 'Account Issues'
      | 'Payment Issues'
      | 'Technical Issues'
      | 'Other';
    priority: 'Low' | 'Medium' | 'High' | 'Urgent';
    emailAddress: string;
    accountNumber?: string;
    issueDescription: string;
    attachments?: File[];
  }): Promise<any> => {
    if (data.attachments && data.attachments.length > 2) {
      throw new Error('Maximum 2 files allowed');
    }

    if (data.attachments) {
      for (const file of data.attachments) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`File ${file.name} exceeds 5MB limit`);
        }
      }
    }

    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('category', data.category);
    formData.append('priority', data.priority);
    formData.append('email', data.emailAddress);
    formData.append('issueDescription', data.issueDescription);

    if (data.accountNumber) {
      formData.append('accountNumber', data.accountNumber);
    }
    if (data.attachments && data.attachments.length > 0) {
      for (const file of data.attachments) {
        formData.append('attachments', file);
      }
    }
    const response = await api.post(
      API_ENDPOINTS.SUPPORT_TICKETS.CREATE,
      formData
    );
    return response.data;
  },

  getAdminTickets: async (queryParams?: string): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.SUPPORT_TICKETS.GET_ALL +
        (queryParams ? `?${queryParams}` : '')
    );
    return response.data;
  },

  getUserTickets: async (queryParams?: string): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.SUPPORT_TICKETS.GET_USER_TICKETS +
        (queryParams ? `?${queryParams}` : '')
    );
    return response.data;
  },

  getTicketById: async (ticketId: string): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.SUPPORT_TICKETS.GET_BY_ID(ticketId)
    );
    return response.data;
  },

  updateUserTicket: async (
    ticketId: string,
    data: {
      category?:
        | 'Trading Issues'
        | 'Account Issues'
        | 'Payment Issues'
        | 'Technical Issues'
        | 'Other';
      issueDescription?: string;
      accountNumber?: string;
      attachments?: File[];
    }
  ): Promise<any> => {
    const formData = new FormData();

    if (data.category) {
      formData.append('category', data.category);
    }
    if (data.issueDescription) {
      formData.append('issueDescription', data.issueDescription);
    }
    if (data.accountNumber) {
      formData.append('accountNumber', data.accountNumber);
    }
    if (data.attachments && data.attachments.length > 0) {
      for (const file of data.attachments) {
        formData.append('attachments', file);
      }
    }

    const response = await api.put(
      API_ENDPOINTS.SUPPORT_TICKETS.USER_UPDATE_TICKET(ticketId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data && !response.data.success) {
      if (
        response.data.message?.includes('Cannot edit ticket') ||
        response.data.message?.includes('already resolved') ||
        response.data.message?.includes('cannot be modified')
      ) {
        throw new Error(
          response.data.message || 'Cannot edit resolved or closed tickets'
        );
      }
    }

    return response.data;
  },

  updateTicket: async (
    ticketId: number,
    data: {
      subject?: string;
      category?:
        | 'Trading Issues'
        | 'Account Issues'
        | 'Payment Issues'
        | 'Technical Issues'
        | 'Other';
      priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
      status?: 'Open' | 'In-progress' | 'Resolved' | 'Closed';
      assignedTo?: number;
      issueDescription?: string;
      message?: string;
    }
  ): Promise<any> => {
    const formData = new FormData();

    if (data.subject) {
      formData.append('subject', data.subject);
    }
    if (data.category) {
      formData.append('category', data.category);
    }
    if (data.priority) {
      formData.append('priority', data.priority);
    }
    if (data.status) {
      const capitalizedStatus =
        data.status.charAt(0).toUpperCase() +
        data.status.slice(1).toLowerCase();
      formData.append('status', capitalizedStatus);
    }
    if (data.assignedTo) {
      formData.append('assignedTo', data.assignedTo.toString());
    }
    if (data.issueDescription) {
      formData.append('issueDescription', data.issueDescription);
    }
    if (data.message) {
      formData.append('message', data.message);
    }

    const response = await api.put(
      API_ENDPOINTS.SUPPORT_TICKETS.UPDATE(ticketId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data && !response.data.success) {
      if (
        response.data.message?.includes('Cannot edit ticket') ||
        response.data.message?.includes('already resolved') ||
        response.data.message?.includes('cannot be modified')
      ) {
        throw new Error(
          response.data.message || 'Cannot edit resolved or closed tickets'
        );
      }
    }

    return response.data;
  },

  deleteTicket: async (ticketId: string): Promise<any> => {
    const response = await api.delete(
      API_ENDPOINTS.SUPPORT_TICKETS.DELETE(ticketId)
    );
    return response.data;
  },

  adminDeleteTicket: async (ticketId: string): Promise<any> => {
    const response = await api.delete(
      API_ENDPOINTS.SUPPORT_TICKETS.ADMIN_DELETE(ticketId)
    );
    return response.data;
  },
};

export const chatbotAPI = {
  sendMessage: async (message: string): Promise<any> => {
    const token = localStorage.getItem('token');
    const response = await api.post(
      API_ENDPOINTS.CHATBOT.SEND_MESSAGE,
      { message },
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      }
    );
    return response.data;
  },
};

export const economicCalendarAPI = {
  getAll: async (params?: any): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.ECONOMIC_CALENDAR.GET_ALL, {
      params: params || { page: 1, limit: 20 },
    });
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.ECONOMIC_CALENDAR.GET_BY_ID(id)
    );
    return response.data;
  },

  create: async (data: any): Promise<any> => {
    const formData = new FormData();
    const dataObj = data as Record<string, any>;
    Object.keys(dataObj).forEach(key => {
      const value = dataObj[key];
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    const response = await api.post(
      API_ENDPOINTS.ECONOMIC_CALENDAR.CREATE,
      formData
    );
    return response.data;
  },

  update: async (id: string, data: any): Promise<any> => {
    const formData = new FormData();
    const dataObj = data as Record<string, any>;
    Object.keys(dataObj).forEach(key => {
      const value = dataObj[key];
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    const response = await api.put(
      API_ENDPOINTS.ECONOMIC_CALENDAR.UPDATE(id),
      formData
    );
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await api.delete(
      API_ENDPOINTS.ECONOMIC_CALENDAR.DELETE(id)
    );
    return response.data;
  },

  getCountries: async (): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.ECONOMIC_CALENDAR.GET_ALL_COUNTRIES
    );
    return response.data;
  },
};

const normalizeMT5Account = (acc: any) => {
  if (!acc || typeof acc !== 'object') return acc;
  const login = acc.mt5Login ?? acc.Login ?? '';
  const name = acc.name ?? acc.Name ?? '';
  const group = acc.group ?? acc.Group ?? '';
  const balance = Number(acc.balance ?? acc.Balance ?? 0);
  const credit = Number(acc.credit ?? acc.Credit ?? 0);
  const leverage = acc.leverage ?? acc.Leverage ?? 100;
  const email = acc.email ?? acc.Email ?? '';
  const country = acc.country ?? acc.Country ?? '';
  const status = acc.status ?? acc.Status ?? '';

  const inferredType =
    acc.accountType ??
    acc.AccountType ??
    (name.toLowerCase().includes('demo') || group.toLowerCase().includes('demo')
      ? 'demo'
      : 'live');

  return {
    ...acc,
    Login: login,
    mt5Login: login,
    Name: name,
    name: name,
    Group: group,
    group: group,
    Balance: balance,
    balance: balance,
    Credit: credit,
    credit: credit,
    Leverage: leverage,
    leverage: leverage,
    Email: email,
    email: email,
    Country: country,
    country: country,
    Status: status,
    status: status,
    AccountType: inferredType,
    accountType: inferredType,
  };
};

const pendingGetUsersPromises: Record<string, Promise<any> | undefined> = {};

export const mt5API = {
  getUsers: async (group?: string): Promise<any> => {
    const key = group || '__ALL__';

    if (pendingGetUsersPromises[key]) {
      return pendingGetUsersPromises[key];
    }

    const fetchPromise = (async () => {
      try {
        const dbResponse = await api.get(API_ENDPOINTS.MT5.GET_USERS, {
          params: { source: 'db', ...(group ? { group } : {}) },
        });

        const rawData = dbResponse?.data || dbResponse;
        if (Array.isArray(rawData)) {
          return rawData.map(normalizeMT5Account);
        }
        return dbResponse?.data || dbResponse;
      } catch (error) {
        console.error('Error fetching MT5 users', error);
        throw error;
      } finally {
        delete pendingGetUsersPromises[key];
      }
    })();

    pendingGetUsersPromises[key] = fetchPromise;
    return fetchPromise;
  },
  getUserByLogin: async (login: string | number): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.MT5.GET_USER(login), {
      params: { source: 'db' },
    });
    const rawData = response?.data?.data ?? response?.data ?? response;
    return normalizeMT5Account(rawData);
  },
  getMyAccounts: async (group?: string): Promise<any> => {
    return mt5API.getUsers(group);
  },
  getGroups: async (): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.MT5.GET_GROUPS);
    return response.data;
  },
  createUser: async (userData: {
    group: string;
    name: string;
    mainPassword?: string;
    investPassword?: string;
    leverage: string | number;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_ACCOUNT, userData);
    return response.data;
  },
  deposit: async (payload: {
    login: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_DEPOSITE, payload);
    return response.data;
  },
  createDeposit: async (payload: {
    login: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_DEPOSITE, payload);
    return response.data;
  },
  withdraw: async (payload: {
    login: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_WITHDRAW, payload);
    return response.data;
  },
  createWithdraw: async (payload: {
    login: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_WITHDRAW, payload);
    return response.data;
  },
  transfer: async (payload: {
    fromLogin: string | number;
    toLogin: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_TRANSFER, payload);
    return response.data;
  },
  createTransfer: async (payload: {
    fromLogin: string | number;
    toLogin: string | number;
    amount: string | number;
    comment?: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CREATE_TRANSFER, payload);
    return response.data;
  },
  getWalletTransactions: async (login?: string | number): Promise<any> => {
    const endpoint =
      login !== undefined && login !== null && login !== ''
        ? API_ENDPOINTS.MT5.Wallet_Transection(login)
        : API_ENDPOINTS.MT5.WALLET_TRANSACTIONS;
    const response = await api.get(endpoint);
    return response.data;
  },
  getLeverageHistoryUser: async (): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.MT5.LEVERAGE_HISTORY);
    return response.data;
  },
  changeLeverage: async (payload: {
    login: string | number;
    leverage: string | number;
    reason: string;
  }): Promise<any> => {
    const response = await api.post(API_ENDPOINTS.MT5.CHANGE_LEVERAGE, {
      login: Number(payload.login),
      leverage: Number(payload.leverage),
      reason: payload.reason,
    });
    return response.data;
  },
};

export default api;
